import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Calendar, Palmtree, Scale, Menu, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { NAV_GROUPS } from '../lib/nav';
import { ICON, Sheet } from './ui';

type Item = { to: string; label: string; icon: LucideIcon; end?: boolean };

const LEFT: Item[] = [
  { to: '/', label: 'Overzicht', icon: LayoutDashboard, end: true },
  { to: '/kalender', label: 'Kalender', icon: Calendar },
];
const RIGHT: Item[] = [
  { to: '/verlof', label: 'Verlof', icon: Palmtree },
  { to: '/afwijkingen', label: 'Controle', icon: Scale },
];
const IN_BAR = new Set([...LEFT, ...RIGHT].map((i) => i.to));

/** Onderbalk op mobiel: vier vaste plekken, de rest onder "Meer". Diensten komen uit het rooster. */
export function BottomNav() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => setMoreOpen(false), [location.pathname]);

  const more = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !IN_BAR.has(i.to)) })).filter((g) => g.items.length);
  const moreActive = more.some((g) => g.items.some((i) => location.pathname.startsWith(i.to)));

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-surface-line/70 bg-slate-950/90 backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Hoofdnavigatie"
      >
        <div className="relative grid h-16 grid-cols-5 items-stretch">
          {LEFT.map((item) => (
            <BarItem key={item.to} item={item} />
          ))}
          {RIGHT.map((item) => (
            <BarItem key={item.to} item={item} />
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className={cn(
              'flex h-full flex-col items-center justify-center gap-1 text-xs font-medium transition-colors duration-fast',
              moreActive ? 'text-slate-50' : 'text-slate-500'
            )}
          >
            <Menu className={ICON.lg} aria-hidden />
            <span>Meer</span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Meer">
        <div className="space-y-5">
          {more.map((g) => (
            <div key={g.title}>
              <div className="t-label mb-1.5">{g.title}</div>
              <div className="list overflow-hidden rounded-lg border border-surface-line">
                {g.items.map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    className={({ isActive }) => cn('row-link px-3', isActive ? 'text-slate-50' : 'text-slate-200')}
                  >
                    <i.icon className={cn(ICON.md, 'text-slate-400')} aria-hidden />
                    <span className="text-sm font-medium">{i.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Sheet>
    </>
  );
}

function BarItem({ item }: { item: Item }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'relative flex h-full flex-col items-center justify-center gap-1 px-1 text-xs font-medium transition-colors duration-fast',
          isActive
            ? 'text-slate-50 after:absolute after:left-1/2 after:top-0 after:h-[3px] after:w-8 after:-translate-x-1/2 after:rounded-b-full after:bg-brand-400'
            : 'text-slate-500'
        )
      }
    >
      <item.icon className={ICON.lg} aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}
